import React from 'react';
import styles from '../Profile.module.css';
import {
  getProfilePicturePreviewMetrics,
  ProfilePictureCropState,
  PROFILE_PICTURE_PREVIEW_SIZE,
  PROFILE_PICTURE_ROUND_PREVIEW_SIZE,
  PROFILE_PICTURE_MAX_ZOOM,
  PROFILE_PICTURE_MIN_ZOOM,
} from '../utils/profilePicture';

interface ProfilePictureEditorModalProps {
  imageSrc: string;
  imageWidth: number;
  imageHeight: number;
  cropState: ProfilePictureCropState;
  error: string | null;
  isSaving: boolean;
  onCancel: () => void;
  onSave: () => void;
  onChangeCropState: (cropState: ProfilePictureCropState) => void;
}

const rangeProgressStyle = (value: number, min: number, max: number) => ({
  '--avatar-editor-range-progress': `${((value - min) / (max - min)) * 100}%`,
}) as React.CSSProperties;

const ProfilePictureEditorModal: React.FC<ProfilePictureEditorModalProps> = ({
  imageSrc,
  imageWidth,
  imageHeight,
  cropState,
  error,
  isSaving,
  onCancel,
  onSave,
  onChangeCropState,
}) => {
  const squarePreviewMetrics = getProfilePicturePreviewMetrics(
    imageWidth,
    imageHeight,
    PROFILE_PICTURE_PREVIEW_SIZE,
    cropState,
  );
  const roundPreviewMetrics = getProfilePicturePreviewMetrics(
    imageWidth,
    imageHeight,
    PROFILE_PICTURE_ROUND_PREVIEW_SIZE,
    cropState,
  );

  return (
    <div
      className={styles.avatarEditorBackdrop}
      onClick={() => {
        if (!isSaving) {
          onCancel();
        }
      }}
    >
      <div
        className={styles.avatarEditorCard}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-picture-editor-title"
        aria-describedby="profile-picture-editor-description"
      >
        <div className={styles.avatarEditorHeader}>
          <div>
            <h2 id="profile-picture-editor-title" className={styles.avatarEditorTitle}>
              Adjust Profile Photo
            </h2>
            <p id="profile-picture-editor-description" className={styles.avatarEditorDescription}>
              Choose the exact square crop you want. We&apos;ll optimize it before uploading so it stays crisp and fast.
            </p>
          </div>
          <button
            type="button"
            className={styles.avatarEditorClose}
            onClick={onCancel}
            disabled={isSaving}
            aria-label="Close profile photo editor"
          >
            ×
          </button>
        </div>

        <div className={styles.avatarEditorPreviewLayout}>
          <div className={styles.avatarEditorPreviewColumn}>
            <div className={styles.avatarEditorPreviewFrame}>
              <img
                src={imageSrc}
                alt="Profile crop preview"
                className={styles.avatarEditorPreviewImage}
                style={squarePreviewMetrics}
                draggable={false}
              />
              <span className={styles.avatarEditorPreviewBadge}>Square crop</span>
            </div>
          </div>

          <div className={styles.avatarEditorRoundPreviewColumn}>
            <div className={styles.avatarEditorRoundPreviewFrame}>
              <img
                src={imageSrc}
                alt="Rounded avatar preview"
                className={styles.avatarEditorPreviewImage}
                style={roundPreviewMetrics}
                draggable={false}
              />
            </div>
            <span className={styles.avatarEditorRoundPreviewLabel}>Live avatar preview</span>
          </div>
        </div>

        <div className={styles.avatarEditorControls}>
          <div className={styles.avatarEditorControlGroup}>
            <label className={styles.avatarEditorControlLabel}>
              <span>Zoom</span>
              <span>{Math.round(cropState.zoom * 100)}%</span>
            </label>
            <input
              type="range"
              min={PROFILE_PICTURE_MIN_ZOOM}
              max={PROFILE_PICTURE_MAX_ZOOM}
              step="0.05"
              value={cropState.zoom}
              disabled={isSaving}
              onChange={(event) => onChangeCropState({
                ...cropState,
                zoom: Number(event.target.value),
              })}
              className={styles.avatarEditorRange}
              style={rangeProgressStyle(
                cropState.zoom,
                PROFILE_PICTURE_MIN_ZOOM,
                PROFILE_PICTURE_MAX_ZOOM,
              )}
            />
          </div>

          <div className={styles.avatarEditorControlGroup}>
            <label className={styles.avatarEditorControlLabel}>
              <span>Horizontal</span>
              <span>{cropState.x}%</span>
            </label>
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              value={cropState.x}
              disabled={isSaving}
              onChange={(event) => onChangeCropState({
                ...cropState,
                x: Number(event.target.value),
              })}
              className={styles.avatarEditorRange}
              style={rangeProgressStyle(cropState.x, 0, 100)}
            />
          </div>

          <div className={styles.avatarEditorControlGroup}>
            <label className={styles.avatarEditorControlLabel}>
              <span>Vertical</span>
              <span>{cropState.y}%</span>
            </label>
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              value={cropState.y}
              disabled={isSaving}
              onChange={(event) => onChangeCropState({
                ...cropState,
                y: Number(event.target.value),
              })}
              className={styles.avatarEditorRange}
              style={rangeProgressStyle(cropState.y, 0, 100)}
            />
          </div>

          <div className={styles.avatarEditorPresetRow}>
            <button
              type="button"
              className={styles.avatarEditorPresetButton}
              disabled={isSaving}
              onClick={() => onChangeCropState({
                x: 50,
                y: 50,
                zoom: 1,
              })}
            >
              Reset
            </button>
            <button
              type="button"
              className={styles.avatarEditorPresetButton}
              disabled={isSaving}
              onClick={() => onChangeCropState({
                ...cropState,
                x: 50,
              })}
            >
              Center X
            </button>
            <button
              type="button"
              className={styles.avatarEditorPresetButton}
              disabled={isSaving}
              onClick={() => onChangeCropState({
                ...cropState,
                y: 50,
              })}
            >
              Center Y
            </button>
          </div>
        </div>

        {error ? (
          <p className={styles.profilePictureMessageError}>{error}</p>
        ) : null}

        <div className={styles.avatarEditorActions}>
          <button
            type="button"
            className={styles.avatarEditorSecondaryButton}
            onClick={onCancel}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.avatarEditorPrimaryButton}
            onClick={onSave}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Photo'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ProfilePictureEditorModal);
